import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';

const LeaderboardContainer = styled.div`
  width: 100%;
  height: 100%;
  padding: 1rem;
  
  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const Title = styled.h1`
  font-size: 1.8rem;
  margin-bottom: 2rem;
  color: ${props => props.theme.colors.primary};
  border-bottom: 1px solid ${props => props.theme.colors.primary};
  padding-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.4rem;
    margin-bottom: 1rem;
  }
`;

const Section = styled.div`
  background-color: #0a0a0a;
  border-radius: 4px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
    margin-bottom: 1rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.2rem;
  margin-bottom: 1rem;
  color: ${props => props.theme.colors.primary};
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const InfoText = styled.p`
  margin-bottom: 1.5rem;
  line-height: 1.6;
`;

const LeaderboardTable = styled.div`
  width: 100%;
  border-radius: 4px;
  overflow: hidden;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 0.5fr 2fr 1fr 1fr;
  padding: 0.75rem 1rem;
  background-color: #161616;
  border-bottom: 2px solid ${props => props.theme.colors.primary};
  font-weight: bold;
  
  @media (max-width: 768px) {
    grid-template-columns: 0.5fr 1.5fr 1fr;
  }
`;

const TableCell = styled.div`
  padding: 0.5rem;
  
  &:first-child {
    text-align: center;
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 0.5fr 2fr 1fr 1fr;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #222;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: rgba(0, 192, 239, 0.05);
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 0.5fr 1.5fr 1fr;
  }
`;

const RankCell = styled(TableCell)`
  font-weight: bold;
`;

const UsernameCell = styled(TableCell)`
  color: ${props => props.theme.colors.primary};
`;

const PointsCell = styled(TableCell)`
  font-weight: bold;
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #666;
`;

// Mock data for the leaderboard
const leaderboardData = [
  { id: 1, rank: 1, username: '@whale_trader', points: 15250, reward: '$125.50' },
  { id: 2, rank: 2, username: '@crypto_master', points: 12800, reward: '$105.20' },
  { id: 3, rank: 3, username: '@bull_market', points: 10500, reward: '$95.75' },
  { id: 4, rank: 4, username: '@token_investor', points: 8900, reward: '$73.10' },
  { id: 5, rank: 5, username: '@defi_guru', points: 7500, reward: '$61.50' },
  { id: 6, rank: 6, username: '@nft_collector', points: 6200, reward: '$50.85' },
  { id: 7, rank: 7, username: '@eth_trader', points: 5100, reward: '$41.80' },
  { id: 8, rank: 8, username: '@sol_fan', points: 4300, reward: '$35.25' },
  { id: 9, rank: 9, username: '@degen_trader', points: 3700, reward: '$30.35' },
  { id: 10, rank: 10, username: '@meme_investor', points: 2900, reward: '$23.75' },
];

const LeaderboardPage: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  // Redirect if not authenticated
  React.useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);
  
  return (
    <DashboardLayout activeMenuItem="leaderboard">
      <LeaderboardContainer>
        <Title>Leaderboard</Title>
        
        <Section>
          <SectionTitle>Top Contributors</SectionTitle>
          
          <InfoText>
            The top contributors are ranked based on their total points earned from bot interactions
            and engagement. The more you engage, the higher your rank will be!
          </InfoText>
          
          <LeaderboardTable>
            <TableHeader>
              <TableCell>Rank</TableCell>
              <TableCell>Username</TableCell>
              <TableCell>Points</TableCell>
              <TableCell style={{ display: window.innerWidth <= 768 ? 'none' : 'block' }}>
                Reward
              </TableCell>
            </TableHeader>
            
            {leaderboardData.length > 0 ? (
              leaderboardData.map((item) => (
                <TableRow key={item.id}>
                  <RankCell>{item.rank}</RankCell>
                  <UsernameCell>{item.username}</UsernameCell>
                  <PointsCell>{item.points.toLocaleString()}</PointsCell>
                  <TableCell style={{ display: window.innerWidth <= 768 ? 'none' : 'block' }}>
                    {item.reward}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <NoDataMessage>
                No leaderboard data available yet. Check back soon!
              </NoDataMessage>
            )}
          </LeaderboardTable>
        </Section>
        
        <Section>
          <SectionTitle>How to Climb the Ranks</SectionTitle>
          
          <InfoText>
            Want to improve your position on the leaderboard? Here's how:
            <ul style={{ marginLeft: '1.5rem', marginTop: '1rem' }}>
              <li>Create helpful content with our bot mentions</li>
              <li>Respond to trending topics by using our utility</li>
              <li>Engage with influencer tweets by adding value with our bot</li>
              <li>Create informative threads showcasing our bot's functionality</li>
              <li>Help new users discover the benefits of our platform</li>
            </ul>
          </InfoText>
        </Section>
      </LeaderboardContainer>
    </DashboardLayout>
  );
};

export default LeaderboardPage; 